import { Fragment, useCallback, useEffect, useState } from 'react';
import { cn } from '@/service/utils/cn';
import { useWindowWidth } from '@/service/hooks/useWindowWidth';
import { ECalendarView } from '@/domain/Activities.domain';
import { Overlay } from '../../Overlay/Overlay';
import { ArrowRightIcon } from '../../Icons/ArrowRightIcon';
import { IconButton } from '../../Buttons/IconButton';
import { RadioInput } from '../../Inputs/RadioInput';
import { SailboatIcon } from '../../Icons/SailboatIcon';
import { FormDivider } from '../../Dividers/FormDivider';
import { ClockIcon } from '../../Icons/ClockIcon';
import { CrossIcon } from '../../Icons/CrossIcon';

type TRadioDropdownProps = {
    className?: string;
    caption: string;
    radioOptions: string[];
    chosenOption?: string;
    setChosenOption?: (arg: string) => void;
    isShown?: boolean;
    showRadioOptions?: () => void;
    hideRadioOptions?: () => void;
};

export const RadioDropdown = ({
    className,
    caption,
    radioOptions,
    chosenOption,
    setChosenOption,
    isShown,
    showRadioOptions,
    hideRadioOptions,
}: TRadioDropdownProps) => {
    const { desktop } = useWindowWidth();

    const [radioOptionsAreShown, setRadioOptionsAreShown] = useState(false);

    useEffect(() => {
        if (isShown) setRadioOptionsAreShown(isShown);
        else setRadioOptionsAreShown(false);
    }, [isShown]);

    const onShow = useCallback(() => {
        if (showRadioOptions) showRadioOptions();
        else setRadioOptionsAreShown(true);
    }, [showRadioOptions]);

    const onHide = useCallback(() => {
        if (hideRadioOptions) hideRadioOptions();
        else setRadioOptionsAreShown(false);
    }, [hideRadioOptions]);

    return (
        <>
            <Overlay isShown={radioOptionsAreShown} onHide={onHide} />
            <div
                className={cn(
                    `relative z-50 flex items-center justify-center ${
                        radioOptionsAreShown
                            ? 'h-max w-36 rounded-[15px_0px_0px_0px] bg-ash-dark dark:bg-dark-3 md:w-40 md:rounded-[15px_15px_0px_0px]'
                            : 'h-[46px] w-max rounded-[36px_0px_0px_36px] bg-gradient-dropdown lg:h-max lg:w-40 lg:rounded-[15px] lg:bg-ash-dark lg:dark:bg-dark-3'
                    }`,
                    className
                )}
            >
                <IconButton
                    className={`w-full rounded-[inherit] bg-[inherit] py-[11px] lg:py-[7px] ${
                        radioOptionsAreShown
                            ? 'flex-row-reverse justify-between border border-b-0 border-brand pl-[19px] pr-0 md:pr-2.5'
                            : '-ml-1 justify-start pl-[6px] pr-2.5 lg:ml-0 lg:flex-row-reverse lg:justify-between lg:pl-[15px] lg:pr-1'
                    }`}
                    onClick={radioOptionsAreShown ? onHide : onShow}
                    label={
                        <p
                            className={`tx-large-medium flex-shrink-0 ${
                                radioOptionsAreShown
                                    ? 'text-gray-2 dark:text-gray-3'
                                    : 'text-white lg:text-gray-2 lg:dark:text-gray-3 '
                            }`}
                        >
                            {caption}
                        </p>
                    }
                    icon={
                        <div className="flex h-6 w-6 items-center justify-center">
                            {!desktop && radioOptionsAreShown ? (
                                <CrossIcon
                                    className="h-3 w-3 md:h-3.5 md:w-3.5 [&_path]:dark:fill-white"
                                    onClick={(e) => (e.stopPropagation(), onHide())}
                                />
                            ) : (
                                <ArrowRightIcon
                                    className={`h-3 w-3 [&_path]:fill-white lg:[&_path]:fill-gray-3 lg:dark:[&_path]:fill-dark-1 ${
                                        radioOptionsAreShown
                                            ? 'lg:rotate-90'
                                            : 'rotate-180 lg:rotate-0'
                                    }`}
                                />
                            )}
                        </div>
                    }
                    data-test-id="toggle-attractions-view"
                />
                {radioOptionsAreShown && (
                    <div className="absolute right-0 top-8 flex w-36 flex-col gap-[6px] rounded-[0px_0px_0px_15px] border border-t-0 border-brand bg-ash-dark pb-3 pt-2 dark:bg-dark-3 md:w-40 md:rounded-b-[15px] lg:left-0">
                        {radioOptions.map((ro, index) => (
                            <Fragment key={ro}>
                                <RadioInput
                                    name="dropdown_option"
                                    id={ro}
                                    className="max-w-full"
                                    label={
                                        <div className="group flex w-full items-center gap-x-[5px] pl-[19px] pr-[11px]">
                                            <div
                                                className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full group-hover:border-brand ${
                                                    chosenOption === ro
                                                        ? "border-[1.3px] border-brand after:h-2 after:w-2 after:rounded-full after:bg-brand after:content-['']"
                                                        : 'border border-gray-3'
                                                }`}
                                            />
                                            <span className="tx-large-medium flex-grow text-gray-2 dark:text-gray-3">
                                                {ro}
                                            </span>
                                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-blue">
                                                {ro === ECalendarView.ACTIVITIES ? (
                                                    <SailboatIcon />
                                                ) : (
                                                    <ClockIcon className="h-6 w-6 [&_path]:fill-white" />
                                                )}
                                            </div>
                                        </div>
                                    }
                                    value={ro}
                                    onChange={(e) => {
                                        setChosenOption?.(e.target.value);
                                        onHide();
                                    }}
                                    data-test-id={`${ro}-option`}
                                />
                                {index !== radioOptions.length - 1 && (
                                    <FormDivider className="ml-[7px] w-[90%] border-[0.8px]" />
                                )}
                            </Fragment>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};
